import Select from '@gerdau/hefesto/Select/Select';
import TextField from '@gerdau/hefesto/TextField/TextField';
import { Button, Form, Input } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterInputContainer } from '../../../../../components/Commons/Filters/styles';
import { ApplicationState } from '../../../../../store';
import {
  loadCustomerBlocks,
  loadCustomerFromId,
  loadCustomerPurchaseReceivers,
  loadCustomersProductDescription
} from '../../../../../store/ducks/customers/actions';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { removeSearchedItem } from '../../../../../store/ducks/long-steel-order-item-search/actions';
import { updateLongSteelShoppingCartSettings } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { uppercaseOnlyFirstLetter } from '../../../../../utils/formatStrings';
import CustomerBlockModalContent from './CustomerBlockModalContent';
import { Container } from './styles';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';

interface OrderSettingsConfigProps {
  emitterDisabled: boolean;
}

const OrderSettingsConfig: React.FC<OrderSettingsConfigProps> = ({
  emitterDisabled
}) => {
  const dispatch = useDispatch();
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const userState = useSelector((state: ApplicationState) => state.user);
  const purchaseCustomers = useSelector((state: ApplicationState) =>
    isUserAdmin
      ? state.customers.customers
      : state.customers.customers.filter((customer) =>
          userState.roles.ordersInputCustomers.includes(customer.customerId)
        )
  );

  const [emitterId, setEmitterId] = useState(shoppingCartState.emitterId);
  const [receiverId, setReceiverId] = useState(shoppingCartState.receiverId);
  const [customerOrder, setCustomerOrder] = useState(
    shoppingCartState.customerOrder
  );

  const [earlyPayment, setEarlyPayment] = useState(false);

  const updateSettings = (
    selectedEmitterId: string,
    selectedReceiverId: string,
    selectedCustomerOrder: string
  ) => {
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: selectedEmitterId,
        receiverId: selectedReceiverId,
        customerOrder: selectedCustomerOrder
      })
    );
  };

  const searchCustomerById = () => {
    dispatch(loadCustomerFromId(emitterId));
    setReceiverId('');
  };

  const getCustomer = (customerId: string): ICustomer => {
    return purchaseCustomers.find(
      (customer) => customer.customerId === customerId
    ) as ICustomer;
  };

  const onSelectEmitter = (customerId: string) => {
    setEarlyPayment(false);
    setEmitterId(customerId);
    setReceiverId('');
    updateSettings(customerId, '', customerOrder);
    if (customerId !== '') {
      const customer = getCustomer(emitterId);
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
      loadPurchaseReceiversIfNeed(customer);
    }

    dispatch(removeSearchedItem([]));
  };

  const onSelectReceiver = (customerId: string) => {
    setReceiverId(customerId);
    updateSettings(emitterId, customerId, customerOrder);
  };

  const loadCustomerBlocksIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.blocks &&
      !customersState.loadingBlocks &&
      !customersState.errorBlocks
    ) {
      dispatch(loadCustomerBlocks(customer.customerId));
    }
  };

  const loadProductDescriptionIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.productsDescriptionResponse &&
      !customersState.loadingProductDescription &&
      !customersState.errorProductDescription
    ) {
      dispatch(loadCustomersProductDescription(customer.customerId));
    }
  };

  const loadPurchaseReceiversIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.purchaseReceivers &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      dispatch(loadCustomerPurchaseReceivers(customer.customerId));
    }
  };

  useEffect(() => {
    const customer = getCustomer(emitterId);

    if (customer && !customersState.error && !customersState.loading) {
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
      loadPurchaseReceiversIfNeed(customer);
    }
  });

  useEffect(() => {
    if (customersState.errorBlocks && emitterId) {
      setCustomerBlockError();
    }
  });

  const changeToEarlyPayment = () => {
    setEarlyPayment(true);
  };

  const setCustomerBlockError = () => {
    setEarlyPayment(false);
    setEmitterId('');
    setReceiverId('');
    updateSettings('', '', customerOrder);
    dispatch(
      addToast({
        type: EToastTypes.DANGER,
        message:
          'Não foi possível validar os dados necessários para o emissor escolhido. Tente novamente mais tarde.',
        durationInMS: 5000
      })
    );
  };

  useEffect(() => {
    const customer = getCustomer(emitterId);
    if (
      customer &&
      receiverId === '' &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      // inicia receiverId se só retornar 1 na lista (purchaseReceivers)
      if (customer.purchaseReceivers?.length === 1) {
        onSelectReceiver(customer.purchaseReceivers[0].code);
      }
    }
  });

  const [canShowCustomerOrderError, setCanShowCustomerOrderError] =
    useState(false);
  const onChangeCustomerOrderInput = (value: string) => {
    setEarlyPayment(false);
    if (value.trim().length >= 34) {
      if (!canShowCustomerOrderError) {
        setCanShowCustomerOrderError(true);
        setTimeout(() => {
          setCanShowCustomerOrderError(false);
        }, 1500);
      }
    } else {
      setCustomerOrder(value);
    }
  };

  const isEmitterDisabled = (): boolean => {
    return (
      customersState.loading ||
      customersState.error ||
      emitterDisabled ||
      !!customersState.loadingProductDescription ||
      !!customersState.loadingPurchaseReceivers
    );
  };

  const isReceiverDisabled = (): boolean => {
    return (
      !emitterId ||
      customersState.loading ||
      customersState.error ||
      !!customersState.loadingPurchaseReceivers
    );
  };

  const emitterOptions = purchaseCustomers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `BP ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const receiverOptions = getCustomer(emitterId)?.purchaseReceivers?.map(
    (receiver) => ({
      label: receiver.name,
      value: receiver.code,
      descriptions: [
        `BP ${receiver.code}`,
        `CNPJ ${receiver.taxNumber}`,
        `${uppercaseOnlyFirstLetter(
          receiver.address
        )} | ${uppercaseOnlyFirstLetter(receiver.city)} - ${
          receiver.state
        } | CEP ${receiver.zipCode}`
      ]
    })
  );

  const getCustomerIdByEmitterId = () => {
    return purchaseCustomers
      .filter((customer) => customer.customerId === emitterId)
      .pop()?.customerId;
  };

  return (
    <Container>
      <div className="customer-order">
        <TextField
          label="Número do pedido"
          name="input-numero-pedido"
          placeholder="Número da ordem de compra (opcional)"
          value={customerOrder}
          onBlur={() =>
            customerOrder !== shoppingCartState.customerOrder
              ? updateSettings(emitterId, receiverId, customerOrder)
              : false
          }
          onChange={(e) => onChangeCustomerOrderInput(e.target.value)}
          maxLength={34}
        />
      </div>

      {isUserAdmin ? (
        <FilterInputContainer>
          <h4>Emissor do pedido</h4>
          <Form>
            <Form.Item
              hasFeedback={customersState.loading}
              validateStatus={
                !customersState.loading && customersState.errorBp ? 'error' : ''
              }
              help={
                customersState.errorBp && !customersState.loading
                  ? 'BP não encontrado'
                  : uppercaseOnlyFirstLetter(getCustomer(emitterId)?.name)
              }
            >
              <Input.Search
                loading={customersState.loading}
                disabled={isEmitterDisabled()}
                placeholder="Digite o BP"
                value={emitterId}
                onChange={(e) => setEmitterId(e.target.value)}
                onSearch={searchCustomerById}
              />
            </Form.Item>
          </Form>
        </FilterInputContainer>
      ) : (
        <Select
          label="Emissor do pedido"
          name="select-emissor-pedido"
          options={emitterOptions}
          value={getCustomerIdByEmitterId()}
          onSelect={(customerId) => onSelectEmitter(customerId as string)}
          isMultipleSelect={false}
          placeholder="- Selecione -"
          disabled={isEmitterDisabled()}
        />
      )}
      <Select
        label="Recebedor do pedido"
        placeholder="- Selecione -"
        options={receiverOptions || []}
        name="select-receiver"
        disabled={isReceiverDisabled()}
        onSelect={(receiver) => onSelectReceiver(receiver as string)}
        value={receiverId}
      />

      {purchaseCustomers.length > 0 && emitterId && (
        <Modal
          visible={
            !!getCustomer(emitterId)?.blocks?.length &&
            !earlyPayment &&
            !customersState.loadingProductDescription
          }
          closable={false}
          width={600}
          title={'Encontramos um problema'}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                onSelectEmitter('');
                setEmitterId('');
              }}
            >
              Cancelar
            </Button>,
            <Button
              key="earlyPayment"
              type="primary"
              onClick={() => changeToEarlyPayment()}
            >
              Pagamento antecipado
            </Button>
          ]}
        >
          {!!getCustomer(emitterId)?.blocks?.length &&
            !earlyPayment &&
            !customersState.loadingProductDescription && (
              <CustomerBlockModalContent customer={getCustomer(emitterId)} />
            )}
        </Modal>
      )}
    </Container>
  );
};

export default OrderSettingsConfig;
